import "../../css/page-loader.css";

import {
  Alert,
  Button,
  GlobalStyle,
  Text,
  ToastContainer,
  openToast,
} from "@vinomofo/components";
import { Cart, CartItem } from "../../contexts/cart/types";
import { ErrorToast, InfoToast, SuccessToast } from "../toast";
import { useCallback, useEffect, useState, ReactNode } from "react";
import { graphql, useStaticQuery } from "gatsby";
import styled, { createGlobalStyle } from "styled-components";

import { Footer } from "../footer";
import { Header } from "../header";
import { Helmet } from "react-helmet";
import NProgress from "nprogress";
import { checkInventoryLevel } from "../../contexts/cart/cart-resolver";
import config from "../../../config.json";
import isBrowser from "../../utils/is-browser";
import { trackAutomaticCartitemEvent } from "../../utils/event-tracking";
import { trackEvent } from "../../utils/track-event";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useCartDiscount } from "../../hooks/cart/use-cart-discount";
import { useLocation } from "@reach/router";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";
import { useStaffDiscount } from "../../hooks/cart/use-staff-discount";

const LOW_INVENTORY_COUNT =
  config[process.env.GATSBY_VM_MARKET].LOW_INVENTORY_COUNT;

NProgress.configure({
  showSpinner: true,
  easing: "ease",
  speed: 650,
  // parent: "#vino-page-loader",
});
type DiscountMap = { [concatString: string]: number };

const PageWrap = styled.div<{ disabled: boolean }>`
  * {
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "")};
  }
`;

const Blocker = styled.div<{ disabled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999999;
  cursor: not-allowed;
  user-select: none;
  filter: grayscale(5%);
  background: #635f5f;
  opacity: 0.4;
`;

const ScrollGlobalStyles = createGlobalStyle`
  // .macos, 
  // .macos *, 
  .non-macos, 
  .non-macos * {
    ::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      transition: all 0.4s ease;
      background: transparent;
    }

    ::-webkit-scrollbar:horizontal {
      width: 5px;
      height: 5px;
      transition: all 0.4s ease;
      background: transparent;
    }

    ::-webkit-scrollbar-track {
      background: transparent;
    }

    ::-webkit-scrollbar-thumb {
      background: #c0c0c0;
      border-radius: 3px;
      width: 5px;
      height: 5px;
      transition: all 0.4s ease;
    }

    &:hover {
      ::-webkit-scrollbar-thumb {
        background: #c0c0c0;
      }
    }
  }
`;

interface LayoutProps {
  children?: ReactNode;
  pathname?: string;
}

export const Layout = ({ children, pathname }: LayoutProps): JSX.Element => {
  const data = useStaticQuery(METADATA_QUERY);
  const { title, description, siteUrl, lang } = data.site.siteMetadata;

  const {
    isLoading: authLoading,
    error: authError,
    user,
    login,
    join,
  } = useAuth();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  const {
    cart,
    isLoading: cartLoading,
    isMerging,
    isRedirecting,
    error: cartError,
    removeItem,
    updateItem,
    soldOutProducts: soldOutProducts_,
    updateSoldOutProductList,
  } = useCart();
  const {
    isProcessing,
    isBuying,
    isRedirecting: oneClickCheckoutRedirecting,
  } = useOneClickCheckout();
  const [prevCart, setPrevCart] = useState<Cart>(undefined);
  const [soldOutProducts, setSoldOutProducts] = useState<Array<CartItem>>();
  const [refreshPage, setRefreshPage] = useState(false);
  const {
    totalCartDiscount,
    cartDiscountItems,
    isLoading: isDiscountLoading,
    fetchTotalCartDiscount,
  } = useCartDiscount(cart);

  const {
    totalStaffDiscount,
    fetchTotalStaffDiscount,
    isLoading: isStaffDiscountLoading,
  } = useStaffDiscount(cart);
  const [localCartDiscountItems, setLocalCartDiscountItems] = useState<
    DiscountMap
  >(cartDiscountItems);

  const isDisabled = isBuying || isProcessing || oneClickCheckoutRedirecting;

  NProgress.configure({
    showSpinner: true,
    easing: "ease",
    speed: 650,
    // parent: "#vino-page-loader"
  });

  useEffect(() => {
    // Add class to non-MacOS user so we can add/customize scroll
    if (typeof window !== "undefined" && isBrowser) {
      const userAgent = window.navigator.userAgent;
      const isMacOS = /Mac OS X/.test(userAgent);
      document.documentElement.classList.add(isMacOS ? "macos" : "non-macos");
    }
  }, []);

  useEffect(() => {
    if (
      authLoading ||
      cartLoading ||
      isMerging ||
      isRedirecting ||
      isProcessing ||
      isBuying ||
      oneClickCheckoutRedirecting
    ) {
      NProgress.start();
    } else {
      NProgress.done();
    }

    if (!(authLoading || cartLoading || isMerging || isRedirecting)) {
      setPrevCart(cart);
    }
  }, [
    authLoading,
    cartLoading,
    cart,
    isMerging,
    isRedirecting,
    isProcessing,
    isBuying,
    oneClickCheckoutRedirecting,
  ]);

  useEffect(() => {
    if ((authError && authError.message) || (cartError && cartError.message)) {
      if (
        authError &&
        authError.message &&
        authError.message === "Invalid state"
      ) {
        setRefreshPage(true);
      } else {
        const errormsg =
          authError && authError.message
            ? "There was an issue logging you in. Please try to Login/Join again"
            : cartError && cartError.message;

        if (cartError?.stack === "guest-coupon") {
          openToast(<GuestCouponError />);
        } else {
          openToast(<ErrorToast message={errormsg} />);
        }
        if (cartError) {
          cartError.message = "";
        }
      }

      if (authError) {
        authError.message = "";
      }
    }
  }, [authError, cartError]);

  useEffect(() => {
    if (refreshPage) {
      if (isBrowser) {
        login({ returnTo: returnTo ? returnTo : "/" })
          .then(() => {})
          .catch(() => {});
      }
    }
  }, [refreshPage]);

  useEffect(() => {
    if (window.innerWidth >= 768) {
      if ((prevCart && prevCart.cartQuantity) < (cart && cart.cartQuantity)) {
        let hasLowStockItem;
        if (prevCart?.lineItems.length > 0) {
          hasLowStockItem = cart?.lineItems.filter((item1) => {
            return prevCart.lineItems.some(
              (item2) =>
                item2.productId === item1.productId &&
                item2.currentStock < LOW_INVENTORY_COUNT &&
                item1.quantity > item2.quantity
            );
          });

          if (hasLowStockItem.length === 0 && cart.lineItems.length <= 2) {
            const latestItemAdded = cart?.lineItems[cart?.lineItems.length - 1];

            hasLowStockItem = [latestItemAdded].filter(
              (item) => item.currentStock < LOW_INVENTORY_COUNT
            );
          }
        } else {
          hasLowStockItem = cart?.lineItems.filter(
            (item) => item.currentStock < LOW_INVENTORY_COUNT
          );
        }

        if (hasLowStockItem.length > 0) {
          openToast(
            <SuccessToast
              label="Hurry!"
              message="Selling FAST! Don't miss out!"
            />
          );
          return;
        }

        openToast(<SuccessToast message="Item added to your cart!" />);
      }
    }
  }, [prevCart, cart]);

  useEffect(() => {
    if (!cartLoading && !soldOutProducts) {
      const cartWithInventoryStatus = async () => {
        try {
          const data = await checkInventoryLevel(cart);
          if (data) {
            const productsSoldOut = data.lineItems.filter((x) => {
              return x.isSoldOut === true || x.availableStock;
            });
            setSoldOutProducts(productsSoldOut);
          }
        } catch (error) {
          console.log(error);
        }
      };

      cartWithInventoryStatus();

      if (soldOutProducts_ && soldOutProducts_.length > 0) {
        if (soldOutProducts_.length === 1) {
          openToast(
            <ErrorToast
              label="Refreshing your cart..."
              message={`Apologies, but the "${soldOutProducts_[0].name}" which we discovered in your previous cart has sold out!`}
            />
          );
        } else {
          openToast(
            <ErrorToast
              label="Refreshing your cart..."
              message={`Apologies, it looks like some items in your previous cart are now unavailable, we've cleaned this up!`}
            />
          );
        }
        updateSoldOutProductList();
      }
    }
  }, [cartLoading, cart]);

  useEffect(() => {
    if (
      !cartLoading &&
      !isMerging &&
      cart?.lineItems?.length > 0 &&
      soldOutProducts
    ) {
      const promiseAll = cart.lineItems.map(async (item) => {
        const {
          id,
          name,
          productId,
          isSoldOut,
          availableStock: updatedStock,
        } = item;
        let toastmsg = null;

        // Remove item if sold-out
        if (
          productId &&
          (isSoldOut ||
            (cart?.soldOutIDs?.length > 0 &&
              cart.soldOutIDs.includes(productId)))
        ) {
          await removeItem(id);
          trackAutomaticCartitemEvent({
            eventName: "Automatic Remove Item From Cart",
            cart: cart,
            idDashname: `${productId}-${name}`,
          });

          toastmsg = {
            remove: `Apologies, but the "${name}" which we discovered in your previous cart has sold out!`,
          };
        }

        // Update item quantity
        if (updatedStock) {
          await updateItem({
            cartItemId: id,
            productId,
            quantity: updatedStock,
          });
          trackAutomaticCartitemEvent({
            eventName: "Automatic Update Cart Item Quantity",
            cart: cart,
            idDashname: `${productId}-${name}`,
            actiontype: "update",
          });

          toastmsg = {
            ...toastmsg,
            update: `"${name}" was found in your previous cart and only has ${updatedStock} left!`,
          };
        }
        return toastmsg;
      });

      // Wait for all removeItem promises to resolve
      Promise.all(promiseAll).then((groupToasMsg) => {
        const nonNullRemoveToastMessages = groupToasMsg
          .filter(
            (msg) =>
              msg &&
              Object?.keys(msg)?.length !== 0 &&
              msg.remove &&
              (msg.remove !== "" || msg.remove !== null)
          )
          .map((i) => i.remove);

        const nonNullUpdateToastMessages = groupToasMsg
          .filter(
            (msg) =>
              msg &&
              Object?.keys(msg)?.length !== 0 &&
              msg.update &&
              (msg.update !== "" || msg.update !== null)
          )
          .map((i) => i.update);

        // Show Remove Toast Message
        if (nonNullRemoveToastMessages.length > 0) {
          if (nonNullRemoveToastMessages.length === 1) {
            openToast(
              <ErrorToast
                label="Refreshing your cart..."
                message={nonNullRemoveToastMessages[0]}
              />
            );
          } else {
            openToast(
              <ErrorToast
                label="Refreshing your cart..."
                message={`Apologies, it looks like some items in your previous cart are now unavailable, we've cleaned this up!`}
              />
            );
          }
          console.log("REMOVE SOLD-OUT COMPLETED ", cart);
        }

        // Show Update Toast Message
        if (nonNullUpdateToastMessages.length > 0) {
          if (nonNullUpdateToastMessages.length === 1) {
            openToast(
              <InfoToast
                label="Updating your cart..."
                message={nonNullUpdateToastMessages[0]}
              />
            );
          } else {
            openToast(
              <InfoToast
                label="Updating your cart..."
                message={`Some items in your previous cart now have limited quantity.`}
              />
            );
          }
          console.log("UPDATE QUANTITY COMPLETED ", cart);
        }
      });
    }
  }, [soldOutProducts]);

  useEffect(() => {
    if (cart && !cartLoading && !isMerging) {
      fetchTotalCartDiscount(cart.lineItems);
      fetchTotalStaffDiscount(cart.lineItems);

      setLocalCartDiscountItems(cartDiscountItems);
      if (
        localCartDiscountItems !== undefined &&
        JSON.stringify(cartDiscountItems) !==
          JSON.stringify(localCartDiscountItems)
      ) {
        cart?.lineItems.forEach(async (item) => {
          const isPublished =
            cartDiscountItems?.[item?.productId + "-" + item?.name] !==
            undefined;
          if (!isPublished) {
            await removeItem(item.id);
            trackAutomaticCartitemEvent({
              eventName: "Automatic Remove Item From Cart",
              cart: cart,
              idDashname: `${item.productId}-${item.name}`,
            });
            const toastmsg = `Apologies, but ${item.name} is no longer available in our store.`;
            openToast(
              <ErrorToast label="Refreshing your cart..." message={toastmsg} />
            );
          }
        });
      }
    }
  }, [
    cart?.lineItems,
    totalCartDiscount,
    cartDiscountItems,
    isDiscountLoading,
    isStaffDiscountLoading,
  ]);

  const handleLogin = useCallback(async () => {
    trackEvent("Login Clicked - Toast", {
      button: "Login",
      location: returnTo ? returnTo : "/",
    });
    login({ returnTo: returnTo ? returnTo : "/" });
  }, [login]);

  const handleSignUp = useCallback(async () => {
    trackEvent("Join Clicked - Toast", {
      button: "Join",
      location: returnTo ? returnTo : "/",
    });
    join({ returnTo: returnTo ? returnTo : "/" });
  }, [join]);

  const GuestCouponError = (): JSX.Element => {
    return (
      <Alert
        padding={1}
        px={1}
        boxShadow="none"
        intent="info"
        label="Signed-in?"
      >
        <Text
          forwardedAs="div"
          fontFamily={"normal"}
          fontSize={[0, 1]}
          fontWeight="500"
        >
          Hmmm. You need to be signed-in to try this coupon. Why not{" "}
          <Button
            onClick={handleLogin}
            color="greenLantern"
            fontFamily={"normal"}
            fontSize={1}
            fontWeight="500"
            type="button"
          >
            login
          </Button>
          {"/"}
          <Button
            onClick={handleSignUp}
            color="greenLantern"
            fontFamily={"normal"}
            fontSize={[0, 1]}
            fontWeight="500"
            type="button"
          >
            join
          </Button>{" "}
          and try again?
        </Text>
      </Alert>
    );
  };

  return (
    <PageWrap disabled={isDisabled}>
      <GlobalStyle />
      <ScrollGlobalStyles />
      <Helmet
        defaultTitle={`Buy Wine Online | ${title}`}
        titleTemplate={`%s | ${title}`}
        htmlAttributes={{ lang }}
        title={`Buy Wine Online | ${title}`}
      >
        <meta name="description" content={description} />
        <link rel="canonical" href={siteUrl} />
      </Helmet>
      <Header pathname={pathname} />
      <ToastContainer position="bottom-right" />
      <main>{children}</main>
      <Footer />
      {isDisabled && <Blocker disabled={isDisabled} />}
    </PageWrap>
  );
};

const METADATA_QUERY = graphql`
  query MetadataQuery {
    site {
      siteMetadata {
        title
        description
        siteUrl
        lang
      }
    }
  }
`;
